import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  Button as MuiButton,
} from '@mui/material';
import { X, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import { Button } from '../../components/Button';
import api from '../../services/api';
import * as Yup from 'yup';
import { formatCep, formatCpfCnpj } from '../../utils';
import { useTranslation } from '../../hooks/useTranslation';
import { tokens } from '../../theme';
import { states, taxRegimes } from '../../constants';

const EMPTY_PARTNER = { fullName: '', email: '', taxvar: '', rg: '' };

const partnerSchema = Yup.object({
  fullName: Yup.string().optional(),
  email: Yup.string().email('E-mail inválido').optional(),
  taxvar: Yup.string().optional(),
  rg: Yup.string().optional(),
});

const nextStepSchema = Yup.object({
  taxvar: Yup.string()
    .transform((v) => v.replace(/\D/g, ''))
    .test('cpf-or-cnpj', 'CPF ou CNPJ inválido', (v) => v?.length === 11 || v?.length === 14)
    .required('Campo obrigatório'),
  zipCode: Yup.string()
    .transform((v) => v.replace(/\D/g, ''))
    .length(8, 'CEP inválido')
    .required('Campo obrigatório'),
  street: Yup.string().trim().required('Campo obrigatório'),
  number: Yup.string().trim().required('Campo obrigatório'),
  neighborhood: Yup.string().trim().required('Campo obrigatório'),
  state: Yup.string().required('Campo obrigatório'),
  city: Yup.string().trim().required('Campo obrigatório'),
});

const validationSchema = Yup.object({
  taxvar: Yup.string()
    .transform((v) => v.replace(/\D/g, ''))
    .test('cpf-or-cnpj', 'CPF ou CNPJ inválido', (v) => v?.length === 11 || v?.length === 14)
    .required('Campo obrigatório'),
  municipalRegistration: Yup.string().trim().required('Campo obrigatório'),
  stateRegistration: Yup.string().trim().required('Campo obrigatório'),
  taxRegime: Yup.string().required('Campo obrigatório'),

  zipCode: Yup.string()
    .transform((v) => v.replace(/\D/g, ''))
    .length(8, 'CEP inválido')
    .required('Campo obrigatório'),
  street: Yup.string().trim().required('Campo obrigatório'),
  number: Yup.string().trim().required('Campo obrigatório'),
  neighborhood: Yup.string().trim().required('Campo obrigatório'),
  complement: Yup.string().optional(),
  state: Yup.string().required('Campo obrigatório'),
  city: Yup.string().trim().required('Campo obrigatório'),

  financialResponsibleName: Yup.string().optional(),
  financialResponsibleEmail: Yup.string().email('E-mail inválido').optional(),
  creditReleaseEmail: Yup.string().email('E-mail inválido').optional(),

  partners: Yup.array().of(partnerSchema),
});

const initialValues = {
  taxvar: '',
  municipalRegistration: '',
  stateRegistration: '',
  taxRegime: '',
  zipCode: '',
  street: '',
  number: '',
  neighborhood: '',
  complement: '',
  state: '',
  city: '',
  financialResponsibleName: '',
  financialResponsibleEmail: '',
  creditReleaseEmail: '',
  partners: [EMPTY_PARTNER],
};

const FieldLabel = ({ label, required, optional, colors }) => (
  <Typography variant="caption" sx={{ color: colors.grey[200], mb: '4px', display: 'block' }}>
    {label}
    {required && <span style={{ color: '#e53935', marginLeft: 2 }}>*</span>}
    {optional && (
      <span style={{ color: colors.grey[400], fontStyle: 'italic', marginLeft: 4 }}>
        (opcional)
      </span>
    )}
  </Typography>
);

const CollapsibleSection = ({ title, open, onToggle, colors, children }) => (
  <Box sx={{ mt: '16px' }}>
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        mb: '6px',
        userSelect: 'none',
        '&:hover .section-title': { opacity: 0.8 },
      }}
    >
      <Typography
        className="section-title"
        variant="body2"
        fontWeight={700}
        sx={{ color: colors.grey[100] }}
      >
        {title}
      </Typography>
      <IconButton size="small" disableRipple sx={{ p: 0, color: colors.grey[300] }}>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </IconButton>
    </Box>
    <Divider sx={{ borderColor: colors.grey[700], mb: '12px' }} />
    <Collapse in={open} timeout="auto" unmountOnExit={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', pb: '4px' }}>{children}</Box>
    </Collapse>
  </Box>
);

const cleanPayload = (values) => {
  const clean = {};
  for (const [key, value] of Object.entries(values)) {
    if (key === 'partners') {
      const cleanedPartners = value
        .map((partner) =>
          Object.fromEntries(
            Object.entries(partner).filter(([, v]) => v !== '' && v !== null && v !== undefined)
          )
        )
        .filter((partner) => Object.keys(partner).length > 0);
      if (cleanedPartners.length > 0) clean.partners = cleanedPartners;
    } else if (value !== '' && value !== null && value !== undefined) {
      clean[key] = value;
    }
  }
  return clean;
};

const CompletarCadastroModal = ({ open, onClose, onSuccess, onNextStep, onSkip, isRequestAccess = false, showSkipStep = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const [sections, setSections] = useState({
    dadosGerais: true,
    endereco: true,
    contatos: false,
    socios: false,
  });

  const toggleSection = (key) => setSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = cleanPayload(values);
        if (isRequestAccess) {
          await api.post('/v1/user/request-access', payload);
        } else {
          await api.put('/v1/user/onboarding', payload);
        }
        onSuccess?.();
        if (!isRequestAccess) {
          onNextStep?.();
        }
      } catch (error) {
        const msg = error?.response?.data?.message || 'Erro ao enviar solicitação. Tente novamente.';
        toast.error(msg, { theme: 'dark' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setCepError('');
    setSections({ dadosGerais: true, endereco: true, contatos: false, socios: false });
    onClose?.();
  };

  const [nextStepLoading, setNextStepLoading] = useState(false);

  // CHANGED: persiste onboarding no backend sem exigir dados preenchidos
  const handleSkipStep = async () => {
    try {
      setNextStepLoading(true);
      await api.put('/v1/user/onboarding', {});
      onSkip?.();
    } catch (error) {
      console.error(error);
      const msg = error?.response?.data?.message || t('messages.skipStepError');
      toast.error(msg, { theme: 'dark' });
    } finally {
      setNextStepLoading(false);
    }
  };

  const handleNextStep = async () => {
    const nextStepFields = [
      'taxvar',
      'zipCode',
      'street',
      'number',
      'neighborhood',
      'state',
      'city',
    ];
    nextStepFields.forEach((f) => formik.setFieldTouched(f, true, false));

    const isValid = await nextStepSchema.isValid(formik.values, { abortEarly: false });
    if (!isValid) {
      setSections((prev) => ({ ...prev, dadosGerais: true, endereco: true }));
      return;
    }

    try {
      setNextStepLoading(true);
      await api.put('/v1/user/onboarding', cleanPayload(formik.values));
      onNextStep?.();
    } catch (error) {
      console.error(error);
    } finally {
      setNextStepLoading(false);
    }
  };

  const isSubmitReady =
    !!formik.values.taxvar &&
    !!formik.values.municipalRegistration?.trim() &&
    !!formik.values.stateRegistration?.trim() &&
    !!formik.values.taxRegime &&
    !!formik.values.zipCode &&
    !!formik.values.street?.trim() &&
    !!formik.values.number?.trim() &&
    !!formik.values.neighborhood?.trim() &&
    !!formik.values.state &&
    !!formik.values.city?.trim();

  const fetchCEP = async (rawCep) => {
    const digits = rawCep.replace(/\D/g, '');
    if (digits.length !== 8) return;

    try {
      setCepLoading(true);
      setCepError('');

      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();

      if (data.erro) {
        setCepError('CEP não encontrado');
        return;
      }

      formik.setValues((prev) => ({
        ...prev,
        street: data.logradouro ?? prev.street,
        neighborhood: data.bairro ?? prev.neighborhood,
        city: data.localidade ?? prev.city,
        state: data.uf ?? prev.state,
        complement: data.complemento ?? prev.complement,
      }));

      ['street', 'neighborhood', 'city', 'state'].forEach((field) =>
        formik.setFieldError(field, undefined)
      );
    } catch (_) {
      setCepError('Erro ao consultar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const handleZipCodeChange = (e) => {
    const masked = formatCep(e.target.value);
    formik.setFieldValue('zipCode', masked);
    setCepError('');
    fetchCEP(masked);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' },
  };

  const fieldProps = (field) => ({
    size: 'small',
    fullWidth: true,
    error: formik.touched[field] && !!formik.errors[field],
    helperText: (formik.touched[field] && formik.errors[field]) ?? ' ',
    onBlur: formik.handleBlur,
    name: field,
    sx: inputSx,
  });

  const partnerFieldProps = (index, field) => ({
    size: 'small',
    fullWidth: true,
    name: `partners.${index}.${field}`,
    value: formik.values.partners[index]?.[field] ?? '',
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched.partners?.[index]?.[field] && !!formik.errors.partners?.[index]?.[field],
    helperText:
      (formik.touched.partners?.[index]?.[field] && formik.errors.partners?.[index]?.[field]) ??
      ' ',
    sx: inputSx,
  });

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#fff',
            backgroundImage: 'none',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: '8px',
            pt: '20px',
            px: '24px',
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ color: colors.grey[100] }}>
            {t('forms.completeData')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: '24px', pt: '8px !important', pb: '8px' }}>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <CollapsibleSection
              title={t('forms.generalData')}
              open={sections.dadosGerais}
              onToggle={() => toggleSection('dadosGerais')}
              colors={colors}
            >
              <Box>
                <FieldLabel label={t('customers.cpfCnpj')} required colors={colors} />
                <TextField
                  {...fieldProps('taxvar')}
                  placeholder={t('forms.cpfCnpj')}
                  value={formik.values.taxvar}
                  onChange={(e) => formik.setFieldValue('taxvar', formatCpfCnpj(e.target.value))}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Box>
                  <FieldLabel label={t('forms.municipalRegistration')} required colors={colors} />
                  <TextField
                    {...fieldProps('municipalRegistration')}
                    placeholder="000000"
                    value={formik.values.municipalRegistration}
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box>
                  <FieldLabel label={t('forms.stateRegistration')} required colors={colors} />
                  <TextField
                    {...fieldProps('stateRegistration')}
                    placeholder="00000000000"
                    value={formik.values.stateRegistration}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>

              <Box>
                <FieldLabel label={t('forms.regimeType')} required colors={colors} />
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  name="taxRegime"
                  value={formik.values.taxRegime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ borderRadius: '8px', fontSize: '14px' }}
                >
                  <MenuItem value="" disabled>
                    {t('forms.select')}
                  </MenuItem>
                  {taxRegimes.map((regime) => (
                    <MenuItem key={regime.value} value={regime.value}>
                      {regime.label}
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ height: '22px' }} />
              </Box>
            </CollapsibleSection>

            <CollapsibleSection
              title={t('forms.address')}
              open={sections.endereco}
              onToggle={() => toggleSection('endereco')}
              colors={colors}
            >
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap="12px">
                <Box>
                  <FieldLabel label={t('forms.zipCode')} required colors={colors} />
                  <TextField
                    size="small"
                    fullWidth
                    name="zipCode"
                    placeholder="00000-000"
                    value={formik.values.zipCode}
                    onChange={handleZipCodeChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.zipCode && !!formik.errors.zipCode) || !!cepError}
                    helperText={
                      (formik.touched.zipCode && formik.errors.zipCode) ?? cepError ?? ' '
                    }
                    sx={inputSx}
                    inputProps={{ maxLength: 9 }}
                    InputProps={{
                      endAdornment: cepLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} thickness={5} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>

                <Box>
                  <FieldLabel label={t('forms.state')} required colors={colors} />
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    name="state"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.state && !!formik.errors.state}
                    sx={{ borderRadius: '8px', fontSize: '14px' }}
                  >
                    <MenuItem value="" disabled>
                      {t('forms.select')}
                    </MenuItem>
                    {states.map((uf) => (
                      <MenuItem key={uf} value={uf}>
                        {uf}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        formik.touched.state && formik.errors.state ? '#e53935' : 'transparent',
                      mt: '3px',
                      display: 'block',
                      ml: '14px',
                    }}
                  >
                    {(formik.touched.state && formik.errors.state) ?? ' '}
                  </Typography>
                </Box>

                <Box>
                  <FieldLabel label={t('forms.city')} required colors={colors} />
                  <TextField
                    {...fieldProps('city')}
                    placeholder={t('forms.city')}
                    value={formik.values.city}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>

              <Box>
                <FieldLabel label={t('forms.place')} required colors={colors} />
                <TextField
                  {...fieldProps('street')}
                  placeholder={t('forms.fullAddress')}
                  value={formik.values.street}
                  onChange={formik.handleChange}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <Box>
                  <FieldLabel label={t('forms.number')} required colors={colors} />
                  <TextField
                    {...fieldProps('number')}
                    placeholder={t('forms.number')}
                    value={formik.values.number}
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box>
                  <FieldLabel label={t('forms.neighborhood')} required colors={colors} />
                  <TextField
                    {...fieldProps('neighborhood')}
                    placeholder={t('forms.neighborhood')}
                    value={formik.values.neighborhood}
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box>
                  <FieldLabel label={t('forms.complement')} optional colors={colors} />
                  <TextField
                    {...fieldProps('complement')}
                    placeholder="Complemento"
                    value={formik.values.complement}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>
            </CollapsibleSection>

            {/* ── Contatos ── */}
            <CollapsibleSection
              title={t('forms.contacts')}
              open={sections.contatos}
              onToggle={() => toggleSection('contatos')}
              colors={colors}
            >
              <Box>
                <FieldLabel label={t('financialEmail')} optional colors={colors} />
                <TextField
                  {...fieldProps('financialResponsibleName')}
                  placeholder={t('forms.fullName')}
                  value={formik.values.financialResponsibleName}
                  onChange={formik.handleChange}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Box>
                  <FieldLabel label={t('forms.financialEmail')} optional colors={colors} />
                  <TextField
                    {...fieldProps('financialResponsibleEmail')}
                    placeholder="email@empresa.com"
                    type="email"
                    value={formik.values.financialResponsibleEmail}
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box>
                  <FieldLabel label={t('forms.creditEmail')} optional colors={colors} />
                  <TextField
                    {...fieldProps('creditReleaseEmail')}
                    placeholder={t('forms.emailPlaceholder')}
                    type="email"
                    value={formik.values.creditReleaseEmail}
                    onChange={formik.handleChange}
                  />
                </Box>
              </Box>
            </CollapsibleSection>

            <CollapsibleSection
              title="Sócios"
              open={sections.socios}
              onToggle={() => toggleSection('socios')}
              colors={colors}
            >
              <FieldArray name="partners">
                {({ push, remove }) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {formik.values.partners.map((partner, index) => (
                      <Box key={index}>
                        {index > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: '8px',
                              mt: '8px',
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: colors.grey[300] }}
                            >
                              {t('forms.partner')} {index + 1}
                            </Typography>
                            <MuiButton
                              size="small"
                              color="error"
                              startIcon={<Trash2 size={14} />}
                              onClick={() => remove(index)}
                              sx={{ textTransform: 'none', fontSize: '12px' }}
                            >
                              {t('buttons.remove')}
                            </MuiButton>
                          </Box>
                        )}

                        <Box>
                          <FieldLabel label={t('forms.fullName')} optional colors={colors} />
                          <TextField
                            {...partnerFieldProps(index, 'fullName')}
                            placeholder={t('forms.partnerFullName')}
                          />
                        </Box>

                        <Box>
                          <FieldLabel label={t('forms.email')} optional colors={colors} />
                          <TextField
                            {...partnerFieldProps(index, 'email')}
                            placeholder={t('forms.emailPlaceholder')}
                            type="email"
                          />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <Box>
                            <FieldLabel label={t('forms.cpf')} optional colors={colors} />
                            <TextField
                              {...partnerFieldProps(index, 'taxvar')}
                              placeholder="000.000.000-00"
                              value={formatCpfCnpj(formik.values.partners[index]?.taxvar ?? '')}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `partners.${index}.taxvar`,
                                  formatCpfCnpj(e.target.value)
                                )
                              }
                            />
                          </Box>
                          <Box>
                            <FieldLabel label={t('forms.document')} optional colors={colors} />
                            <TextField
                              {...partnerFieldProps(index, 'rg')}
                              placeholder="00.000.000-0"
                            />
                          </Box>
                        </Box>

                        {index < formik.values.partners.length - 1 && (
                          <Divider sx={{ borderColor: colors.grey[700], mt: '8px', mb: '4px' }} />
                        )}
                      </Box>
                    ))}

                    <MuiButton
                      startIcon={<Plus size={16} />}
                      onClick={() => push(EMPTY_PARTNER)}
                      sx={{
                        alignSelf: 'flex-start',
                        color: colors.greenAccent[500],
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        mt: '4px',
                        px: 0,
                        '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
                      }}
                    >
                      {t('forms.addPartner')}
                    </MuiButton>
                  </Box>
                )}
              </FieldArray>
            </CollapsibleSection>
          </Box>
        </DialogContent>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '24px',
            py: '16px',
          }}
        >
          <Box
            onClick={
              nextStepLoading
                ? undefined
                : showSkipStep
                ? handleSkipStep
                : handleNextStep
            }
            sx={{
              cursor: nextStepLoading ? 'default' : 'pointer',
              color: nextStepLoading ? colors.grey[500] : colors.greenAccent[500],
              '&:hover': { opacity: nextStepLoading ? 1 : 0.8 },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: 'inherit', textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {showSkipStep
                ? t('onboarding.skipStep')
                : nextStepLoading
                ? t('common.saving')
                : t('onboarding.nextStep')}
            </Typography>
          </Box>

          <Button
            variant="contained"
            disabled={formik.isSubmitting || !isSubmitReady}
            fullWidth={false}
            onClick={formik.handleSubmit}
            sx={{
              fontWeight: 700,
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {formik.isSubmitting ? t('common.sending') : t('onboarding.sendRequestAccess')}
          </Button>
        </Box>
      </Dialog>
    </FormikProvider>
  );
};

export default CompletarCadastroModal;
